import React, { useEffect, useMemo, useState, useRef } from "react";
import Card from "components/card";
import { IoEye, IoFilter, IoSearch, IoChevronBack, IoChevronForward, IoTrash, IoCall, IoCalendar } from "react-icons/io5";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "utils/api";
import {
  addLeadNote,
  addLeadSchedule,
  deleteLeadNote,
  deleteLeadSchedule,
  getLeadNoteScheduleDetails,
} from "../leadApi";
import { useNotificationContext } from "context/NotificationContext";
import { usePermission } from "hooks/usePermission";

import LeadStatusBadge from "components/badges/LeadStatusBadge";
import LeadPriorityBadge from "components/badges/LeadPriorityBadge";
import AddLeadModal from "components/modal/AddLeadModal";
import LeadFilters from "components/filters/LeadFilters";
import SortDropdown from "components/dropdown/SortDropdown";
import FolderDropdown from "components/dropdown/FolderDropdown";

const columnHelper = createColumnHelper();
const CALL_ACTIVITY_STORAGE_KEY = "lead_call_activity";

export default function ComplexTable(props) {
  const {
    tableData,
    isLoading,
    error,
    currentPage,
    pageSize,
    totalPages,
    onPageChange,
    onPageSizeChange,
    filters,
    onFilterChange,
    onFiltersChange,
    onClearFilters,
    isAddModalOpen,
    onCloseAddModal,
    onLeadCreated,
    showToast,
    total,
  } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshNotifications } = useNotificationContext();
  const [sorting, setSorting] = React.useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const activeFilterCount = React.useMemo(() => {
    if (!filters) return 0;
    try {
      return Object.entries(filters).filter(([k, v]) => {
        if (v === undefined || v === null) return false;
        if (typeof v === "string") return v.trim() !== "";
        if (Array.isArray(v)) return v.length > 0;
        return true;
      }).length;
    } catch (e) {
      return 0;
    }
  }, [filters]);
  const tableWrapperRef = useRef(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, lead: null });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isCallerModalOpen, setIsCallerModalOpen] = useState(false);
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [noteInput, setNoteInput] = useState("");
  const [noteErrors, setNoteErrors] = useState({});
  const [noteSuccessMessage, setNoteSuccessMessage] = useState("");
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [scheduleDateTime, setScheduleDateTime] = useState("");
  const [scheduleNote, setScheduleNote] = useState("");
  const [scheduleErrors, setScheduleErrors] = useState({});
  const [scheduleSuccessMessage, setScheduleSuccessMessage] = useState("");
  const [isAddingSchedule, setIsAddingSchedule] = useState(false);
  const [isFetchingActivityDetails, setIsFetchingActivityDetails] = useState(false);
  const [activityFetchError, setActivityFetchError] = useState("");
  const [isDeletingActivity, setIsDeletingActivity] = useState(false);
  const [deletingActivityId, setDeletingActivityId] = useState("");
  const [activityDeleteStatus, setActivityDeleteStatus] = useState({ type: "", message: "" });
  const [activityDeleteConfirm, setActivityDeleteConfirm] = useState({
    show: false,
    type: null,
    itemId: "",
    title: "",
    message: "",
  });
  const { can } = usePermission();
  const canDeleteLead = can("lead_management", "delete");

  const [activityByLead, setActivityByLead] = useState({});
  const [isUpdatingCallStatus, setIsUpdatingCallStatus] = useState(false);
  const [hasCheckedWaitingOnEntry, setHasCheckedWaitingOnEntry] = useState(false);
  const [isCheckingWaitingLead, setIsCheckingWaitingLead] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(CALL_ACTIVITY_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          setActivityByLead(parsed);
        }
      }
    } catch (storageError) {
      console.error("Failed to read call activity from storage", storageError);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(CALL_ACTIVITY_STORAGE_KEY, JSON.stringify(activityByLead));
    } catch (storageError) {
      console.error("Failed to save call activity", storageError);
    }
  }, [activityByLead]);

  useEffect(() => {
    const normalizedPath = (location?.pathname || "").replace(/\/$/, "");
    if (normalizedPath === "/admin/lead-management") {
      setHasCheckedWaitingOnEntry(false);
    }
  }, [location?.pathname]);

  const handleAddLead = async () => {
    if (showToast) {
      showToast("Lead created successfully", "success");
    }
    if (onLeadCreated) {
      await onLeadCreated();
    }
  };

  const handleViewLead = async (lead) => {
    const leadId = lead.id || lead._id;
    try {
      // Fetch full lead details from API
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/leads/${leadId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.data) {
        // Navigate to lead profile with full details
        navigate(`/admin/lead-management/lead-profile/${leadId}`, {
          state: { lead: response.data.data, from: location.pathname }
        });
      }
    } catch (error) {
      console.error("Error fetching lead details:", error);
      if (showToast) {
        showToast("Failed to load lead details", "error");
      }
      // Fallback: navigate with basic details
      navigate(`/admin/lead-management/lead-profile/${leadId}`, { state: { lead, from: location.pathname } });
    }
  };

  const handleDeleteLead = (lead) => {
    setDeleteConfirm({ show: true, lead });
  };

  const getLeadKey = (lead) => {
    if (!lead) return "";
    return String(lead.id || lead._id || lead.phone || lead.email || lead.name || "unknown");
  };

  const getLeadActivity = (lead) => {
    const key = getLeadKey(lead);
    return activityByLead[key] || { notes: [], schedules: [] };
  };

  const launchDialer = (phone) => {
    if (!phone) {
      if (showToast) {
        showToast("Phone number not found for this lead", "error");
      }
      return;
    }
    window.location.href = `tel:${String(phone).trim()}`;
  };

  const getCallingStatus = (lead) => {
    const candidates = [
      lead?.call_status,
      lead?.callStatus,
      lead?.calling_status,
      lead?.callingStatus,
      lead?.call_status?.status,
      lead?.callStatus?.status,
      lead?.calling_status?.status,
      lead?.callingStatus?.status,
      lead?.call_status?.value,
      lead?.callStatus?.value,
      lead?.calling_status?.value,
      lead?.callingStatus?.value,
    ];
    const firstString = candidates.find((item) => typeof item === "string" && item.trim());
    return firstString ? firstString.trim().toLowerCase() : "";
  };

  const getLeadsFromResponse = (responseData) => {
    const possibleCollections = [
      responseData?.data?.data,
      responseData?.data?.allLeads?.data,
      responseData?.data?.allLeads,
      responseData?.data,
      responseData,
    ];

    for (const collection of possibleCollections) {
      if (Array.isArray(collection)) return collection;
    }

    return [];
  };

  const normalizeNoteErrorPath = (path = "") => {
    const normalized = String(path).toLowerCase().replace(/[^a-z]/g, "");
    if (normalized.includes("leadid")) return "leadId";
    if (normalized.includes("note")) return "note";
    return "submit";
  };

  const mapNoteApiErrors = (apiErrors = []) => {
    if (!Array.isArray(apiErrors)) return {};

    return apiErrors.reduce((acc, item) => {
      const fieldKey = normalizeNoteErrorPath(item?.path);
      const message = item?.msg || item?.message;
      if (!message) return acc;

      acc[fieldKey] = acc[fieldKey] ? `${acc[fieldKey]} ${message}` : message;
      return acc;
    }, {});
  };

  const normalizeScheduleErrorPath = (path = "") => {
    const normalized = String(path).toLowerCase().replace(/[^a-z]/g, "");
    if (normalized.includes("leadid")) return "leadId";
    if (normalized.includes("scheduledtime") || normalized.includes("scheduledfor")) {
      return "scheduledTime";
    }
    if (normalized.includes("note")) return "note";
    return "submit";
  };

  const mapScheduleApiErrors = (apiErrors = []) => {
    if (!Array.isArray(apiErrors)) return {};

    return apiErrors.reduce((acc, item) => {
      const fieldKey = normalizeScheduleErrorPath(item?.path);
      const message = item?.msg || item?.message;
      if (!message) return acc;

      acc[fieldKey] = acc[fieldKey] ? `${acc[fieldKey]} ${message}` : message;
      return acc;
    }, {});
  };

  const getScheduleFromApiData = (payload, fallbackSchedule) => {
    const record =
      payload?.data?.schedule ||
      payload?.data?.scheduledCall ||
      payload?.data ||
      payload?.schedule ||
      null;

    return {
      id:
        record?._id ||
        record?.id ||
        `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      scheduleId: record?._id || record?.id || "",
      scheduledFor:
        record?.scheduledFor ||
        record?.scheduledTime ||
        fallbackSchedule.scheduledFor,
      note: record?.note || fallbackSchedule.note,
      createdAt: record?.createdAt || new Date().toISOString(),
    };
  };

  const normalizeActivityDetails = (payload) => {
    const notes = Array.isArray(payload?.notes)
      ? payload.notes
        .map((item, index) => {
          if (!item || !item.note) return null;
          const createdAt = item?.date || item?.createdAt || new Date().toISOString();
          return {
            id: item?._id || `${createdAt}-${index}-${Math.random().toString(36).slice(2, 7)}`,
            noteId: item?._id || item?.id || "",
            content: item.note,
            createdAt,
          };
        })
        .filter(Boolean)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      : [];

    const schedules = Array.isArray(payload?.schedules)
      ? payload.schedules
        .map((item, index) => {
          if (!item) return null;
          const scheduledFor = item?.scheduledTime || item?.scheduledFor;
          if (!scheduledFor) return null;

          return {
            id:
              item?._id ||
              `${scheduledFor}-${item?.createdAt || Date.now()}-${index}-${Math.random()
                .toString(36)
                .slice(2, 7)}`,
            scheduleId: item?._id || item?.id || "",
            scheduledFor,
            note: item?.note || "",
            createdAt: item?.createdAt || new Date().toISOString(),
            scheduleStatus: item?.scheduleStatus || "",
          };
        })
        .filter(Boolean)
        .sort((a, b) => new Date(b.scheduledFor) - new Date(a.scheduledFor))
      : [];

    return { notes, schedules };
  };

  const openCallPopup = (lead) => {
    setSelectedLead(lead);
    setIsCallerModalOpen(true);
    setIsActivityModalOpen(false);
    setNoteInput("");
    setNoteErrors({});
    setNoteSuccessMessage("");
    setScheduleDateTime("");
    setScheduleNote("");
    setScheduleErrors({});
    setScheduleSuccessMessage("");
    setActivityDeleteStatus({ type: "", message: "" });
  };

  const updateLeadCallStatus = async ({ leadId, nextStatus, showSuccessToast = true }) => {
    if (!leadId || !nextStatus || isUpdatingCallStatus) return false;

    setIsUpdatingCallStatus(true);
    try {
      const token = localStorage.getItem("token");

      const response = await axios.put(
        `${API_BASE_URL}/leads/call-status`,
        {
          leadId,
          call_status: nextStatus,
        },
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "Content-Type": "application/json",
          },
        }
      );

      setSelectedLead((prev) => {
        if (!prev) return prev;
        const prevId = prev.id || prev._id;
        if (String(prevId) !== String(leadId)) return prev;

        return {
          ...prev,
          call_status: response?.data?.data?.call_status || nextStatus,
        };
      });

      if (showSuccessToast && showToast) {
        const successMessage = response?.data?.message || `Call status updated to ${nextStatus}`;
        showToast(successMessage, "success");
      }

      if (onLeadCreated) {
        await onLeadCreated();
      }

      return true;
    } catch (err) {
      const errorData = err?.response?.data;
      const validationMessages = Array.isArray(errorData?.errors)
        ? errorData.errors
          .map((item) => item?.msg)
          .filter(Boolean)
        : [];
      const errorMessage = validationMessages.length
        ? validationMessages.join("\n")
        : errorData?.message || "Failed to update call status";

      if (showToast) {
        showToast(errorMessage, "error");
      }

      return false;
    } finally {
      setIsUpdatingCallStatus(false);
    }
  };

  const handleCallLead = async (lead) => {
    openCallPopup(lead);
    const leadId = lead?.id || lead?._id;
    await updateLeadCallStatus({
      leadId,
      nextStatus: "waiting",
      showSuccessToast: false,
    });
    launchDialer(lead?.phone);
  };

  const updateCallStatus = async (nextStatus, { closeOnSuccess = false } = {}) => {
    if (!selectedLead) return;

    const leadId = selectedLead.id || selectedLead._id;
    if (!leadId) {
      if (showToast) showToast("Unable to update call status", "error");
      return;
    }

    const updated = await updateLeadCallStatus({ leadId, nextStatus });
    if (updated && closeOnSuccess) {
      closeCallFlow();
    }
  };

  useEffect(() => {
    const normalizedPath = (location?.pathname || "").replace(/\/$/, "");
    if (normalizedPath !== "/admin/lead-management") return;
    if (hasCheckedWaitingOnEntry || isCheckingWaitingLead || isLoading) return;

    const waitingOnCurrentData = Array.isArray(tableData)
      ? tableData.find((lead) => getCallingStatus(lead) === "waiting")
      : null;

    if (waitingOnCurrentData) {
      openCallPopup(waitingOnCurrentData);
      setHasCheckedWaitingOnEntry(true);
      return;
    }

    const findWaitingLeadFromApi = async () => {
      setIsCheckingWaitingLead(true);
      try {
        const token = localStorage.getItem("token");

        const requestVariants = [
          { page: 1, limit: 1, call_status: "waiting" },
          { page: 1, limit: 1, calling_status: "waiting" },
          { page: 1, limit: 200 },
        ];

        let waitingLead = null;
        for (const params of requestVariants) {
          const response = await axios.get(`${API_BASE_URL}/leads`, {
            params,
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          });

          const leads = getLeadsFromResponse(response?.data);
          if (!Array.isArray(leads) || leads.length === 0) continue;

          waitingLead = leads.find((lead) => getCallingStatus(lead) === "waiting");
          if (waitingLead) break;
        }

        if (waitingLead) {
          openCallPopup(waitingLead);
        }
      } catch (err) {
        // Ignore waiting lookup errors to avoid interrupting lead page load.
      } finally {
        setHasCheckedWaitingOnEntry(true);
        setIsCheckingWaitingLead(false);
      }
    };

    findWaitingLeadFromApi();
  }, [location?.pathname, tableData, isLoading, hasCheckedWaitingOnEntry, isCheckingWaitingLead]);

  const closeCallFlow = () => {
    setIsCallerModalOpen(false);
    setIsActivityModalOpen(false);
    setSelectedLead(null);
    setNoteInput("");
    setNoteErrors({});
    setNoteSuccessMessage("");
    setIsAddingNote(false);
    setScheduleErrors({});
    setScheduleSuccessMessage("");
    setIsAddingSchedule(false);
    setIsDeletingActivity(false);
    setDeletingActivityId("");
    setActivityDeleteStatus({ type: "", message: "" });
    setActivityDeleteConfirm({ show: false, type: null, itemId: "", title: "", message: "" });
  };

  const openActivityModal = async () => {
    if (!selectedLead) return;

    const leadId = selectedLead.id || selectedLead._id;
    const leadKey = getLeadKey(selectedLead);

    setIsCallerModalOpen(false);
    setIsActivityModalOpen(true);
    setNoteErrors({});
    setNoteSuccessMessage("");
    setScheduleErrors({});
    setScheduleSuccessMessage("");
    setActivityFetchError("");
    setActivityDeleteStatus({ type: "", message: "" });

    if (!leadId) {
      setActivityFetchError("Lead ID is required to fetch activity details");
      return;
    }

    setIsFetchingActivityDetails(true);
    try {
      const responseData = await getLeadNoteScheduleDetails(leadId);
      const normalizedActivity = normalizeActivityDetails(responseData?.data || {});

      setActivityByLead((prev) => ({
        ...prev,
        [leadKey]: normalizedActivity,
      }));
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || "Failed to load notes and schedules for this lead";
      setActivityFetchError(errorMessage);
      if (showToast) {
        showToast(errorMessage, "error");
      }
    } finally {
      setIsFetchingActivityDetails(false);
    }
  };

  const addNote = async () => {
    if (!selectedLead || isAddingNote) return;

    const leadId = selectedLead.id || selectedLead._id;
    const value = noteInput.trim();

    if (!leadId) {
      setNoteErrors({ leadId: "Lead ID is required" });
      return;
    }

    if (!value) {
      setNoteErrors({ note: "Note is required" });
      return;
    }

    const leadKey = getLeadKey(selectedLead);
    const current = getLeadActivity(selectedLead);

    setIsAddingNote(true);
    setNoteErrors({});
    setNoteSuccessMessage("");

    try {
      const responseData = await addLeadNote({ leadId, note: value });
      const notesFromApi = responseData?.data?.notes;

      const normalizedApiNotes = Array.isArray(notesFromApi)
        ? notesFromApi
          .map((item) => {
            if (!item || !item.note) return null;
            return {
              id:
                item?._id ||
                `${item?.date || Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
              noteId: item?._id || item?.id || "",
              content: item.note,
              createdAt: item.date || new Date().toISOString(),
            };
          })
          .filter(Boolean)
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        : null;

      const fallbackNotes = [
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          content: value,
          createdAt: new Date().toISOString(),
        },
        ...current.notes,
      ];

      setActivityByLead((prev) => ({
        ...prev,
        [leadKey]: {
          notes: normalizedApiNotes && normalizedApiNotes.length > 0 ? normalizedApiNotes : fallbackNotes,
          schedules: current.schedules,
        },
      }));

      setNoteInput("");
      setNoteSuccessMessage(responseData?.message || "Lead note added successfully");

      if (showToast) {
        showToast(responseData?.message || "Lead note added successfully", "success");
      }
    } catch (err) {
      const errorData = err?.response?.data;
      const mappedErrors = mapNoteApiErrors(errorData?.errors);

      if (Object.keys(mappedErrors).length > 0) {
        setNoteErrors(mappedErrors);
      } else {
        const fallbackMessage = errorData?.message || "Failed to add note";
        setNoteErrors({ submit: fallbackMessage });
      }

      if (showToast) {
        showToast(errorData?.message || "Failed to add note", "error");
      }
    } finally {
      setIsAddingNote(false);
    }
  };

  const requestActivityDelete = ({ type, itemId }) => {
    if (!selectedLead || !itemId || !type) return;

    setActivityDeleteConfirm({
      show: true,
      type,
      itemId,
      title: type === "note" ? "Delete Note?" : "Delete Schedule?",
      message:
        type === "note"
          ? "This note will be removed from this call activity timeline."
          : "This schedule entry will be removed from this lead timeline.",
    });
  };

  const closeActivityDeleteConfirm = () => {
    setActivityDeleteConfirm({ show: false, type: null, itemId: "", title: "", message: "" });
  };

  const confirmActivityDelete = async () => {
    if (!selectedLead || !activityDeleteConfirm?.itemId || !activityDeleteConfirm?.type) {
      closeActivityDeleteConfirm();
      return;
    }

    const leadId = selectedLead.id || selectedLead._id;
    if (!leadId) {
      setActivityDeleteStatus({ type: "error", message: "Lead ID is missing for delete operation" });
      closeActivityDeleteConfirm();
      return;
    }

    setIsDeletingActivity(true);
    setDeletingActivityId(activityDeleteConfirm.itemId);

    const leadKey = getLeadKey(selectedLead);
    const current = getLeadActivity(selectedLead);

    try {
      if (activityDeleteConfirm.type === "note") {
        await deleteLeadNote(leadId, activityDeleteConfirm.itemId);
        const nextNotes = current.notes.filter(
          (item) => item.noteId !== activityDeleteConfirm.itemId && item.id !== activityDeleteConfirm.itemId
        );
        setActivityByLead((prev) => ({
          ...prev,
          [leadKey]: {
            notes: nextNotes,
            schedules: current.schedules,
          },
        }));
        setActivityDeleteStatus({ type: "success", message: "Note deleted successfully" });
        setNoteSuccessMessage("Note deleted successfully");
        if (showToast) {
          showToast("Note deleted successfully", "success");
        }
      }

      if (activityDeleteConfirm.type === "schedule") {
        await deleteLeadSchedule(leadId, activityDeleteConfirm.itemId);
        const nextSchedules = current.schedules.filter(
          (item) => item.scheduleId !== activityDeleteConfirm.itemId && item.id !== activityDeleteConfirm.itemId
        );
        setActivityByLead((prev) => ({
          ...prev,
          [leadKey]: {
            notes: current.notes,
            schedules: nextSchedules,
          },
        }));
        setActivityDeleteStatus({ type: "success", message: "Schedule deleted successfully" });
        setScheduleSuccessMessage("Schedule deleted successfully");
        if (showToast) {
          showToast("Schedule deleted successfully", "success");
        }
        // Refresh notifications after deleting a schedule
        await refreshNotifications();
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.message || "Failed to delete item";
      setActivityDeleteStatus({ type: "error", message: errorMessage });
      if (showToast) {
        showToast(errorMessage, "error");
      }
    } finally {
      setIsDeletingActivity(false);
      setDeletingActivityId("");
      closeActivityDeleteConfirm();
    }
  };

  const addSchedule = async () => {
    if (!selectedLead || isAddingSchedule) return;

    const leadId = selectedLead.id || selectedLead._id;
    if (!leadId) {
      setScheduleErrors({ leadId: "Lead ID is required" });
      return;
    }

    if (!scheduleDateTime) {
      setScheduleErrors({ scheduledTime: "scheduledTime is required" });
      return;
    }

    const leadKey = getLeadKey(selectedLead);
    const current = getLeadActivity(selectedLead);
    const payload = {
      leadId,
      scheduledTime: new Date(scheduleDateTime).toISOString(),
      note: scheduleNote.trim(),
    };

    const fallbackSchedule = {
      scheduledFor: payload.scheduledTime,
      note: payload.note,
    };

    setIsAddingSchedule(true);
    setScheduleErrors({});
    setScheduleSuccessMessage("");

    try {
      const responseData = await addLeadSchedule(payload);
      const createdSchedule = getScheduleFromApiData(responseData, fallbackSchedule);

      setActivityByLead((prev) => ({
        ...prev,
        [leadKey]: {
          notes: current.notes,
          schedules: [createdSchedule, ...current.schedules],
        },
      }));

      setScheduleSuccessMessage(responseData?.message || "Schedule added successfully");
      setScheduleDateTime("");
      setScheduleNote("");

      if (showToast) {
        showToast(responseData?.message || "Schedule added successfully", "success");
      }

      const statusUpdated = await updateLeadCallStatus({
        leadId,
        nextStatus: "scheduled",
        showSuccessToast: false,
      });

      if (statusUpdated) {
        // Refresh notifications immediately after scheduling a call
        // This updates the notification count and list in real-time
        await refreshNotifications();
        closeCallFlow();
      }
    } catch (err) {
      const errorData = err?.response?.data;
      const mappedErrors = mapScheduleApiErrors(errorData?.errors);

      if (Object.keys(mappedErrors).length > 0) {
        setScheduleErrors(mappedErrors);
      } else {
        const fallbackMessage = errorData?.message || "Failed to add schedule";
        setScheduleErrors({ submit: fallbackMessage });
      }

      if (showToast) {
        showToast(errorData?.message || "Failed to add schedule", "error");
      }
    } finally {
      setIsAddingSchedule(false);
    }
  };

  const closeDeleteModal = () => {
    if (deleteLoading) return;
    setDeleteConfirm({ show: false, lead: null });
  };

  const confirmDeleteLead = async () => {
    if (!deleteConfirm.lead) return;

    setDeleteLoading(true);
    try {
      const token = localStorage.getItem("token");
      const leadId = deleteConfirm.lead.id || deleteConfirm.lead._id;

      const response = await axios.delete(`${API_BASE_URL}/leads/${leadId}`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const successMessage = response?.data?.message || "Lead deleted successfully";
      if (showToast) {
        showToast(successMessage, "success");
      }
      closeDeleteModal();
      if (onLeadCreated) {
        await onLeadCreated();
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.message || "Failed to delete lead";
      if (showToast) {
        showToast(errorMessage, "error");
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const uniqueSources = useMemo(() => {
    const values = tableData.map((lead) => lead.source).filter(Boolean);
    return Array.from(new Set(values)).sort();
  }, [tableData]);

  const uniquePriorities = useMemo(() => {
    const values = tableData.map((lead) => lead.priority).filter(Boolean);
    return Array.from(new Set(values)).sort();
  }, [tableData]);

  const uniqueStatuses = useMemo(() => {
    const values = tableData.map((lead) => lead.status).filter(Boolean);
    return Array.from(new Set(values)).sort();
  }, [tableData]);

  const currentLeadActivity = getLeadActivity(selectedLead);

  const getInitials = (name) => {
    if (!name) return "NA";
    const parts = String(name).trim().split(" ").filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
  };

  const renderDismissibleLabel = ({ message, type = "success", onClose, className = "mt-2" }) => {
    if (!message) return null;

    const isError = type === "error";
    const toneClassName =
      isError
        ? "border-l-4 border-red-500 border-y border-r border-red-300 bg-red-100 text-red-800 shadow-sm dark:border-red-400 dark:bg-red-500/20 dark:text-red-200"
        : "border-l-4 border-emerald-500 border-y border-r border-emerald-300 bg-emerald-100 text-emerald-800 shadow-sm dark:border-emerald-400 dark:bg-emerald-500/20 dark:text-emerald-200";

    return (
      <div className={`${className} flex items-start justify-between gap-2 rounded-lg px-3 py-2 text-xs font-semibold ${toneClassName}`}>
        <div className="flex items-start gap-2">
          <span
            className={`mt-[2px] inline-flex min-w-[54px] items-center justify-center rounded-full px-2 py-[2px] text-[10px] font-bold uppercase tracking-wide ${isError
              ? "bg-red-600 text-white dark:bg-red-500"
              : "bg-emerald-600 text-white dark:bg-emerald-500"
              }`}
          >
            {isError ? "Failed" : "Success"}
          </span>
          <span>{message}</span>
        </div>
        {onClose ? (
          <button
            onClick={onClose}
            className="flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold opacity-90 hover:bg-black/10 hover:opacity-100"
            aria-label="Close status"
            title="Close"
          >
            x
          </button>
        ) : null}
      </div>
    );
  };

  const columns = [
    columnHelper.accessor("name", {
      id: "name",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">NAME</p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-navy-700 dark:text-white">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("phone", {
      id: "phone",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">PHONE</p>
      ),
      cell: (info) => (
        <p className="text-sm font-medium text-navy-700 dark:text-white">{info.getValue()}</p>
      ),
    }),
    columnHelper.accessor("source", {
      id: "source",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">SOURCE</p>
      ),
      cell: (info) => (
        <p className="text-sm font-medium text-navy-700 dark:text-white">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("status", {
      id: "status",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">STATUS</p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          <LeadStatusBadge status={info.getValue()} />
        </div>
      ),
    }),
    columnHelper.accessor("priority", {
      id: "priority",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">PRIORITY</p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          <LeadPriorityBadge priority={info.getValue()} />
        </div>
      ),
    }),
    columnHelper.display({
      id: "actions",
      header: () => (
        <p className="text-sm font-bold text-gray-600 dark:text-white">ACTION</p>
      ),
      cell: (info) => (
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => handleCallLead(info.row.original)}
            className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg dark:text-emerald-400 dark:hover:bg-emerald-900/20 transition-colors"
            title="Call Lead"
            aria-label="Call Lead"
          >
            <IoCall className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleViewLead(info.row.original)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg dark:text-blue-400 dark:hover:bg-blue-900/20 transition-colors"
            title="View Lead"
          >
            <IoEye className="h-4 w-4" />
          </button>
          {canDeleteLead && (
            <button
              onClick={() => handleDeleteLead(info.row.original)}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg dark:text-red-400 dark:hover:bg-red-900/20 transition-colors"
              title="Delete Lead"
            >
              <IoTrash className="h-4 w-4" />
            </button>
          )}
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <>
      <Card extra={"w-full h-full px-6 pb-6 sm:overflow-x-auto"}>
        <div className="flex flex-wrap items-center justify-between gap-4 pt-4">
          <div className="flex flex-wrap items-center gap-4 min-w-0">
            <div className="text-xl font-bold text-navy-700 dark:text-white whitespace-nowrap">
              Lead Details
            </div>
            <div className="min-w-[260px] max-w-sm flex-1">
              <div className="relative">
                <IoSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  value={filters.search || ""}
                  onChange={(e) => onFilterChange("search", e.target.value)}
                  placeholder="Search name, email or phone"
                  className="h-10 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 text-sm text-gray-700 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <FolderDropdown 
              type="Lead"
              value={filters?.folderId || ""}
              onChange={(val) => onFilterChange("folderId", val)}
            />
            <SortDropdown
              value={filters?.sort || "latest_updated"}
              onChange={(val) => onFilterChange("sort", val)}
            />
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`relative flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${showFilters
                ? 'bg-brand-500 text-white hover:bg-brand-600'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <IoFilter className="h-5 w-5" />
              Filters
              {activeFilterCount > 0 && (
                <span className="absolute -top-2 -right-2 inline-flex items-center justify-center rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-semibold text-white">
                  {typeof total === 'number' ? total : activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <LeadFilters
            searchTerm=""
            onSearchChange={() => { }}
            filters={filters}
            onFilterChange={onFilterChange}
            onFiltersChange={onFiltersChange}
            onClearFilters={onClearFilters}
            uniqueSources={uniqueSources}
            uniquePriorities={uniquePriorities}
            uniqueStatuses={uniqueStatuses}
            showSearch={false}
          />
        )}

        <div ref={tableWrapperRef} className={`relative mt-4 overflow-x-scroll xl:overflow-x-hidden ${table.getRowModel().rows.length === 0 ? 'min-h-[180px]' : ''}`}>
          <table className="w-full">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="!border-px !border-gray-400">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      onClick={header.column.getToggleSortingHandler()}
                      className={`border-b-[1px] border-gray-200 pt-4 pb-2 pr-4 ${header.id === 'actions'
                        ? 'sticky right-0 bg-white dark:bg-gray-800 z-10 min-w-[170px] w-[170px] text-center'
                        : 'text-start cursor-pointer'
                        }`}
                    >
                      <div className="items-center justify-between text-xs text-gray-200">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: "",
                          desc: "",
                        }[header.column.getIsSorted()] ?? null}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={columns.length} className="text-center py-8 text-gray-500 dark:text-gray-400">
                    Loading leads...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={columns.length} className="text-center py-8 text-red-500">
                    {error}
                  </td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No leads found matching your criteria.
                  </td>
                </tr>
              ) : (
                table
                  .getRowModel()
                  .rows.map((row) => {
                    let priorityClass = "hover:bg-gray-50 dark:hover:bg-gray-800";
                    if (row.original.priority === "Urgent") {
                      priorityClass = "bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30";
                    } else if (row.original.priority === "High") {
                      priorityClass = "bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/30";
                    }
                    return (
                      <tr key={row.id} className={priorityClass}>
                        {row.getVisibleCells().map((cell) => {
                          return (
                            <td
                              key={cell.id}
                              className={`border-white/0 py-3 pr-4 ${cell.column.id === 'actions'
                                ? 'sticky right-0 bg-white dark:bg-gray-800 z-10 min-w-[170px] w-[170px] text-center'
                                : 'min-w-[150px]'
                                }`}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-center mt-6 gap-6">
          {/* Rows per page selector - Segmented control */}
          <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-full p-1">
            <span className="text-sm text-gray-600 dark:text-gray-400 mr-2 px-2">Rows:</span>
            {[10, 20, 50].map((size) => (
              <button
                key={size}
                onClick={() => onPageSizeChange && onPageSizeChange(size)}
                className={`px-3 py-1 text-sm font-medium rounded-full transition-colors ${pageSize === size
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
              >
                {size}
              </button>
            ))}
          </div>

          {/* Pagination navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange && onPageChange(Math.max(1, currentPage - 1))}
              disabled={isLoading || currentPage <= 1}
              className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <IoChevronBack className="h-4 w-4" />
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-400 min-w-[60px] text-center">
              {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => onPageChange && onPageChange(Math.min(totalPages, currentPage + 1))}
              disabled={isLoading || currentPage >= totalPages}
              className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <IoChevronForward className="h-4 w-4" />
            </button>
          </div>
        </div>
      </Card>

      <AddLeadModal
        isOpen={isAddModalOpen}
        onClose={onCloseAddModal}
        onSave={handleAddLead}

      />

      {deleteConfirm.show && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/30 backdrop-blur-sm p-4"
          onClick={closeDeleteModal}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl dark:bg-gray-800"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-navy-700 dark:text-white">Delete Lead?</h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              This action will permanently remove
              {deleteConfirm.lead?.name ? ` ${deleteConfirm.lead.name}` : " this lead"}.
            </p>
            <div className="mt-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button
                onClick={closeDeleteModal}
                disabled={deleteLoading}
                className="w-full sm:w-auto rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteLead}
                disabled={deleteLoading}
                className="w-full sm:w-auto rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50"
              >
                {deleteLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isCallerModalOpen && selectedLead && (
        <div
          className="fixed inset-0 z-[72] flex items-end sm:items-start lg:items-center justify-center overflow-y-auto bg-slate-900/25 backdrop-blur-sm p-3 sm:p-4"
          style={{
            backgroundColor: "rgba(15, 23, 42, 0.28)",
            backdropFilter: "blur(7px)",
            WebkitBackdropFilter: "blur(7px)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="my-3 w-full max-w-lg max-h-[calc(100vh-1.5rem)] overflow-y-auto rounded-2xl bg-gradient-to-br from-white via-slate-50 to-blue-50 p-4 shadow-xl sm:my-5 sm:max-h-[calc(100vh-2.5rem)] dark:from-gray-800 dark:via-gray-800 dark:to-slate-800 sm:p-6"
            style={{
              background: "linear-gradient(145deg, #ffffff 0%, #f8fbff 56%, #f4fff8 100%)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3">
              {selectedLead.profileImage ? (
                <img
                  src={selectedLead.profileImage}
                  alt={selectedLead.name || "lead"}
                  className="h-16 w-16 rounded-full object-cover"
                />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-500 text-lg font-bold text-white">
                  {getInitials(selectedLead.name)}
                </div>
              )}
              <div className="min-w-0">
                <h3 className="truncate text-lg font-bold text-navy-700 dark:text-white">{selectedLead.name || "Unknown Lead"}</h3>
                <p className="truncate text-sm text-gray-600 dark:text-gray-300">{selectedLead.phone || "No phone"}</p>
                {selectedLead.email && (
                  <p className="truncate text-xs text-gray-500 dark:text-gray-400">{selectedLead.email}</p>
                )}
              </div>
            </div>

            <div className="mt-4 rounded-xl bg-gray-50 p-3 dark:bg-gray-700/40">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300">Calling Details</p>
              <div className="mt-2 grid grid-cols-1 gap-2 text-sm text-gray-700 dark:text-gray-200 sm:grid-cols-2">
                <p><span className="font-semibold">Source:</span> {selectedLead.source || "-"}</p>
                <p><span className="font-semibold">Status:</span> {selectedLead.status || "-"}</p>
                <p><span className="font-semibold">Priority:</span> {selectedLead.priority || "-"}</p>
                <p><span className="font-semibold">Care Of:</span> {selectedLead?.careOf?.name || "-"}</p>
                <p className="sm:col-span-2">
                  <span className="font-semibold">Call Status:</span> {getCallingStatus(selectedLead) || "-"}
                </p>
              </div>
            </div>

            <div className="mt-4 rounded-xl border border-gray-200 bg-white/70 p-3 dark:border-gray-700 dark:bg-gray-800/60">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300">Update Call Status</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  onClick={() => updateCallStatus("pending", { closeOnSuccess: true })}
                  disabled={isUpdatingCallStatus}
                  className="min-w-[118px] rounded-lg bg-amber-500 px-3 py-1.5 text-center text-xs font-semibold text-white shadow-sm hover:bg-amber-600 disabled:opacity-60"
                >
                  {isUpdatingCallStatus ? "Updating..." : "Cancel Call"}
                </button>
                <button
                  onClick={() => updateCallStatus("completed", { closeOnSuccess: true })}
                  disabled={isUpdatingCallStatus}
                  className="min-w-[118px] rounded-lg bg-red-600 px-3 py-1.5 text-center text-xs font-semibold text-white shadow-sm hover:bg-red-700 disabled:opacity-60"
                >
                  {isUpdatingCallStatus ? "Updating..." : "Mark Completed"}
                </button>
              </div>
            </div>

            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                onClick={() => launchDialer(selectedLead.phone)}
                className="w-full rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-600 sm:w-auto"
              >
                Call Again
              </button>
              <button
                onClick={openActivityModal}
                className="w-full rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 sm:w-auto"
              >
                Add Notes & Schedule
              </button>
            </div>
          </div>
        </div>
      )}

      {isActivityModalOpen && selectedLead && (
        <div
          className="fixed inset-0 z-[73] flex items-end sm:items-start lg:items-center justify-center overflow-y-auto bg-slate-900/25 backdrop-blur-sm p-3 sm:p-4"
          style={{
            backgroundColor: "rgba(15, 23, 42, 0.28)",
            backdropFilter: "blur(7px)",
            WebkitBackdropFilter: "blur(7px)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="my-3 w-full max-w-2xl max-h-[calc(100vh-1.5rem)] overflow-y-auto rounded-2xl bg-gradient-to-br from-white via-slate-50 to-emerald-50 p-4 shadow-xl sm:my-5 sm:max-h-[calc(100vh-2.5rem)] dark:from-gray-800 dark:via-gray-800 dark:to-slate-800 sm:p-6"
            style={{
              background: "linear-gradient(145deg, #ffffff 0%, #f8fbff 56%, #f1fff7 100%)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-bold text-navy-700 dark:text-white">Call Notes & Next Schedule</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">{selectedLead.name || "Lead"} • {selectedLead.phone || "No phone"}</p>
              </div>
              <button
                onClick={() => launchDialer(selectedLead.phone)}
                className="rounded-lg bg-emerald-500 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-600"
              >
                Call
              </button>
            </div>

            {isFetchingActivityDetails ? (
              <div className="mt-3 rounded-lg border border-brand-200 bg-brand-50/60 p-2 text-xs font-medium text-brand-700 dark:border-brand-400/40 dark:bg-brand-500/10 dark:text-brand-200">
                Loading latest notes and schedules...
              </div>
            ) : null}

            {activityFetchError ? (
              renderDismissibleLabel({
                message: activityFetchError,
                type: "error",
                onClose: () => setActivityFetchError(""),
                className: "mt-3",
              })
            ) : null}

            {activityDeleteStatus.message ? (
              renderDismissibleLabel({
                message: activityDeleteStatus.message,
                type: activityDeleteStatus.type === "error" ? "error" : "success",
                onClose: () => setActivityDeleteStatus({ type: "", message: "" }),
                className: "mt-3",
              })
            ) : null}

            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <div className="rounded-xl border border-gray-200 p-3 dark:border-gray-700">
                <p className="text-sm font-semibold text-navy-700 dark:text-white">Add Call Note</p>
                {noteErrors.submit ? (
                  renderDismissibleLabel({
                    message: noteErrors.submit,
                    type: "error",
                    onClose: () =>
                      setNoteErrors((prev) => ({
                        ...prev,
                        submit: "",
                      })),
                  })
                ) : null}
                {noteErrors.leadId ? (
                  <p className="mt-2 text-xs text-red-500">{noteErrors.leadId}</p>
                ) : null}
                {noteSuccessMessage ? (
                  renderDismissibleLabel({
                    message: noteSuccessMessage,
                    type: "success",
                    onClose: () => setNoteSuccessMessage(""),
                  })
                ) : null}
                <textarea
                  value={noteInput}
                  onChange={(e) => {
                    setNoteInput(e.target.value);
                    setNoteSuccessMessage("");
                    setNoteErrors((prev) => ({
                      ...prev,
                      note: "",
                      submit: "",
                    }));
                  }}
                  placeholder="Write what happened in this call..."
                  className="mt-2 h-24 w-full rounded-lg border border-gray-200 p-2 text-sm outline-none focus:border-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                {noteErrors.note ? (
                  <p className="mt-1 text-xs text-red-500">{noteErrors.note}</p>
                ) : null}
                <button
                  onClick={addNote}
                  disabled={isAddingNote}
                  className="mt-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
                >
                  {isAddingNote ? "Adding..." : "Add Note"}
                </button>

                <div className="mt-3 max-h-44 overflow-y-auto rounded-lg bg-gray-50 p-2 dark:bg-gray-700/40">
                  {currentLeadActivity.notes.length === 0 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-300">No previous notes yet.</p>
                  ) : (
                    currentLeadActivity.notes.map((note) => (
                      <div key={note.id} className="mb-2 rounded-lg bg-white p-2 text-sm dark:bg-gray-700">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-gray-700 dark:text-gray-100">{note.content}</p>
                          <button
                            onClick={() => requestActivityDelete({ type: "note", itemId: note.noteId || note.id })}
                            disabled={isDeletingActivity}
                            className="flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                            title="Delete note"
                            aria-label="Delete note"
                          >
                            {isDeletingActivity && deletingActivityId === (note.noteId || note.id) ? "..." : "x"}
                          </button>
                        </div>
                        <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-300">
                          {new Date(note.createdAt).toLocaleString()}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 p-3 dark:border-gray-700">
                <p className="flex items-center gap-2 text-sm font-semibold text-navy-700 dark:text-white">
                  <IoCalendar className="h-4 w-4" />
                  Schedule Next Call
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {/* <button
                    onClick={() => updateCallStatus("completed")}
                    disabled={isUpdatingCallStatus}
                    className="min-w-[118px] rounded-lg bg-emerald-600 px-3 py-1.5 text-center text-xs font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60"
                  >
                    Mark Completed
                  </button> */}
                </div>
                <div className="mt-2 rounded-lg border border-dashed border-brand-300 bg-brand-50/40 p-3 dark:border-brand-400/40 dark:bg-brand-500/10">
                  {scheduleErrors.submit ? (
                    renderDismissibleLabel({
                      message: scheduleErrors.submit,
                      type: "error",
                      onClose: () =>
                        setScheduleErrors((prev) => ({
                          ...prev,
                          submit: "",
                        })),
                      className: "mb-2",
                    })
                  ) : null}
                  {scheduleSuccessMessage ? (
                    renderDismissibleLabel({
                      message: scheduleSuccessMessage,
                      type: "success",
                      onClose: () => setScheduleSuccessMessage(""),
                      className: "mb-2",
                    })
                  ) : null}
                  {scheduleErrors.leadId ? (
                    <p className="mb-2 text-xs text-red-500">{scheduleErrors.leadId}</p>
                  ) : null}
                  <input
                    type="datetime-local"
                    value={scheduleDateTime}
                    onChange={(e) => {
                      setScheduleDateTime(e.target.value);
                      setScheduleSuccessMessage("");
                      setScheduleErrors((prev) => ({
                        ...prev,
                        scheduledTime: "",
                        submit: "",
                      }));
                    }}
                    className="w-full rounded-lg border border-gray-200 p-2 text-sm outline-none focus:border-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                  {scheduleErrors.scheduledTime ? (
                    <p className="mt-1 text-xs text-red-500">{scheduleErrors.scheduledTime}</p>
                  ) : null}
                  <input
                    type="text"
                    value={scheduleNote}
                    onChange={(e) => {
                      setScheduleNote(e.target.value);
                      setScheduleSuccessMessage("");
                      setScheduleErrors((prev) => ({
                        ...prev,
                        note: "",
                        submit: "",
                      }));
                    }}
                    placeholder="Optional note for next call"
                    className="mt-2 w-full rounded-lg border border-gray-200 p-2 text-sm outline-none focus:border-brand-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                  {scheduleErrors.note ? (
                    <p className="mt-1 text-xs text-red-500">{scheduleErrors.note}</p>
                  ) : null}
                  <button
                    onClick={addSchedule}
                    disabled={isAddingSchedule}
                    className="mt-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
                  >
                    {isAddingSchedule ? "Adding..." : "Add Schedule"}
                  </button>
                </div>

                <div className="mt-3 max-h-44 overflow-y-auto rounded-lg bg-gray-50 p-2 dark:bg-gray-700/40">
                  {currentLeadActivity.schedules.length === 0 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-300">No previous schedules yet.</p>
                  ) : (
                    currentLeadActivity.schedules.map((item) => (
                      <div key={item.id} className="mb-2 rounded-lg bg-white p-2 text-sm dark:bg-gray-700">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-semibold text-gray-800 dark:text-gray-100">
                            {new Date(item.scheduledFor).toLocaleString()}
                          </p>
                          <button
                            onClick={() =>
                              requestActivityDelete({ type: "schedule", itemId: item.scheduleId || item.id })
                            }
                            disabled={isDeletingActivity}
                            className="flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                            title="Delete schedule"
                            aria-label="Delete schedule"
                          >
                            {isDeletingActivity && deletingActivityId === (item.scheduleId || item.id)
                              ? "..."
                              : "x"}
                          </button>
                        </div>
                        {item.note ? (
                          <p className="mt-1 text-gray-600 dark:text-gray-200">{item.note}</p>
                        ) : null}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                onClick={() => {
                  setIsActivityModalOpen(false);
                  setIsCallerModalOpen(true);
                }}
                className="w-full rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 sm:w-auto"
              >
                Back
              </button>
              <button
                onClick={() => updateCallStatus("completed", { closeOnSuccess: true })}
                disabled={isUpdatingCallStatus}
                className="w-full rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 sm:w-auto"
              >
                {isUpdatingCallStatus ? "Updating..." : "Done"}
              </button>
            </div>
          </div>
        </div>
      )}

      {activityDeleteConfirm.show && (
        <div
          className="fixed inset-0 z-[74] flex items-center justify-center bg-slate-900/30 backdrop-blur-sm p-4"
          style={{
            backgroundColor: "rgba(15, 23, 42, 0.32)",
            backdropFilter: "blur(7px)",
            WebkitBackdropFilter: "blur(7px)",
          }}
          onClick={closeActivityDeleteConfirm}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-gradient-to-br from-white via-slate-50 to-rose-50 p-5 shadow-xl dark:from-gray-800 dark:via-gray-800 dark:to-slate-800"
            style={{
              background: "linear-gradient(145deg, #ffffff 0%, #f8fbff 56%, #fff4f4 100%)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-navy-700 dark:text-white">{activityDeleteConfirm.title}</h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{activityDeleteConfirm.message}</p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">This action cannot be undone.</p>

            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                onClick={closeActivityDeleteConfirm}
                disabled={isDeletingActivity}
                className="w-full rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 sm:w-auto"
              >
                Cancel
              </button>
              <button
                onClick={confirmActivityDelete}
                disabled={isDeletingActivity}
                className="w-full rounded-lg bg-red-500 px-4 py-2 text-sm font-semibold text-white hover:bg-red-600 sm:w-auto"
              >
                {isDeletingActivity ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
